import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import moment from "moment";
import { colors } from "../../../theme";
import { Box, Button, Flex, Icon, useTranslation, Text } from "@familyzone/component-library";
import React, { useLayoutEffect, useEffect, useMemo, useRef, useState } from "react";
import ComponentLoading from "../../../modules/ComponentLoading";
import type { SignUpAggregation, DailySignUpDataPoint, AggregatedSignUpDataPoint, SignUpStatsResponse } from "../../../types/Community";
import type { TableColumn } from "../../../types/table";
import { CHART_LICENSE } from "../../../constants/charts";

const SIGN_UPS_CSV_FILENAME = "sign-ups-over-time-export.csv";

const SIGN_UPS_CSV_COLUMNS: TableColumn[] = [
  { headerText: "Date", columnName: "date", exportable: true },
  { headerText: "New Sign Ups", columnName: "newSignUps", exportable: true },
  { headerText: "Connected Students", columnName: "connectedStudents", exportable: true },
];

interface SignUpsOverTimeChartProps {
  data: SignUpStatsResponse;
  isLoading: boolean;
  isError: boolean;
  isDownloading?: boolean;
  onDownload?: (columns: TableColumn[], filename: string) => void;
}

interface ChartDataPoint {
  date: Date;
  count: number;
  label: string;
}

export default function SignUpsOverTimeChart({
  data,
  isLoading,
  isError,
  isDownloading,
  onDownload,
}: SignUpsOverTimeChartProps): JSX.Element {
  const { t } = useTranslation();
  const [aggregation, setAggregation] = useState<SignUpAggregation>("monthly");
  const [chartReady, setChartReady] = useState<boolean>(false);
  const chartRef = useRef<am4charts.XYChart | null>(null);
  const isEmptyState = data.data.length === 0;

  const chartData: ChartDataPoint[] = useMemo((): ChartDataPoint[] => {
    const dailyData: DailySignUpDataPoint[] = data.data;
    const aggregatedData: AggregatedSignUpDataPoint[] = aggregateSignUpData(dailyData, aggregation);
    return aggregatedData.map(
      (point: AggregatedSignUpDataPoint): ChartDataPoint => ({
        date: new Date(point.startDate),
        count: point.count,
        label: point.label,
      })
    );
  }, [data.data, aggregation]);

  // Dispose chart only on component unmount
  useLayoutEffect(() => {
    // Add license only once
    am4core.addLicense(CHART_LICENSE);
    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isLoading) return;

    let chart = chartRef.current;

    // Initialize chart only once
    if (!chart) {
      chart = am4core.create("signups-over-time-chart", am4charts.XYChart);
      chartRef.current = chart;

      chart.paddingLeft = 0;
      chart.paddingRight = 0;
      chart.fontFamily = "Lato";
      chart.fontSize = 12;
      chart.interpolationDuration = 0;
      chart.defaultState.transitionDuration = 0;

      // X Axis
      const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = "label";
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.grid.template.strokeOpacity = 0;
      categoryAxis.renderer.minGridDistance = 60;
      categoryAxis.renderer.line.stroke = am4core.color(colors.neutrals["30"]);
      categoryAxis.renderer.line.strokeOpacity = 0.2;
      categoryAxis.renderer.line.strokeWidth = 1.5;
      categoryAxis.renderer.labels.template.fill = am4core.color(colors.neutrals["500"]);
      categoryAxis.renderer.labels.template.fontWeight = "800";
      categoryAxis.cursorTooltipEnabled = false;

      // Y Axis
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.min = 0;
      valueAxis.renderer.grid.template.stroke = am4core.color(colors.neutrals["30"]);
      valueAxis.renderer.grid.template.strokeOpacity = 0.5;
      valueAxis.renderer.grid.template.strokeWidth = 1.5;
      valueAxis.renderer.minGridDistance = 40;
      valueAxis.renderer.labels.template.fill = am4core.color(colors.neutrals["500"]);
      valueAxis.renderer.labels.template.fontSize = "12px";
      valueAxis.renderer.labels.template.fontWeight = "800";
      valueAxis.renderer.labels.template.marginLeft = -8;
      valueAxis.cursorTooltipEnabled = false;

      // Series
      const series = chart.series.push(new am4charts.LineSeries());
      series.dataFields.valueY = "count";
      series.dataFields.categoryX = "label";
      series.tooltipText = "{count} sign-ups";
      series.strokeWidth = 2;
      series.stroke = am4core.color(colors.brand["400"]);

      series.interpolationDuration = 0;
      series.defaultState.transitionDuration = 0;

      // Bullets
      const bullet = series.bullets.push(new am4charts.CircleBullet());
      bullet.circle.radius = 4;
      bullet.circle.fill = am4core.color(colors.brand["400"]);
      bullet.circle.stroke = am4core.color(colors.brand["400"]);
      bullet.circle.strokeWidth = 2;

      // Tooltip Styling
      if (series.tooltip) {
        series.tooltip.getFillFromObject = false;
        series.tooltip.background.fill = am4core.color(colors.neutrals["900"]);
        series.tooltip.background.strokeWidth = 0;
        series.tooltip.background.cornerRadius = 4;
        series.tooltip.pointerOrientation = "vertical";
        series.tooltip.label.fill = am4core.color(colors.neutrals["0"]);
        series.tooltip.label.fontSize = 12;
      }

      // Cursor
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.lineY.disabled = true;
      chart.cursor.lineX.disabled = true;

      chart.events.once("ready", () => setChartReady(true));
    }

    // Update Data (runs every time chartData changes)
    chart.data = chartData;

    const valueAxis = chart.yAxes.getIndex(0) as am4charts.ValueAxis;
    const series = chart.series.getIndex(0);

    if (!series) return;
    const bullet = series.bullets.getIndex(0);

    if (!isEmptyState) {
      // Show Line
      series.strokeOpacity = 1;
      // Show Dots
      if (bullet) bullet.disabled = false;
      // Show Tooltip
      if (series.tooltip) series.tooltip.disabled = false;
      // Reset Y-Axis Max (let it auto-calculate)
      if (valueAxis) valueAxis.max = undefined;
    } else {
      // Hide Line
      series.strokeOpacity = 0;
      // Hide Dots
      if (bullet) bullet.disabled = true;
      // Hide Tooltip
      if (series.tooltip) series.tooltip.disabled = true;
      // Force Y-Axis Max to 40
      if (valueAxis) valueAxis.max = 40;
    }
  }, [chartData, isLoading, isEmptyState]);

  const selectedButtonStyle = {
    _focus: { outline: "none", boxShadow: "none" },
    _focusVisible: {
      outline: "none",
      boxShadow: `0 0 0 2px ${colors.brand["400"]}`,
    },
    backgroundColor: colors.neutrals["500"],
    _hover: { backgroundColor: colors.neutrals["600"] },
    transition: "background-color 100ms ease-in-out",
  };

  const defaultButtonStyle = {
    _focus: { outline: "none", boxShadow: "none" },
    _focusVisible: {
      outline: "none",
      boxShadow: `0 0 0 2px ${colors.brand["400"]}`,
    },
    backgroundColor: "transparent",
    _hover: { backgroundColor: colors.neutrals["20"] },
    transition: "background-color 100ms ease-in-out",
  };

  const toggleContainerStyle = {
    border: "2px solid",
    borderColor: colors.neutrals["40"],
    borderRadius: "24px",
    backgroundColor: colors.neutrals["0"],
    padding: "4px",
    height: "44px",
  };

  const showLoading = isLoading || (!isEmptyState && !chartReady);
  const showChart = chartReady && !isLoading && !isError;

  const isWeeklySelected = aggregation === "weekly";
  const isMonthlySelected = aggregation === "monthly";

  return (
    <Box bg="neutrals.0" pt="sp16" px="sp24" borderRadius="md" boxShadow={30}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb="sp16" minW="6px">
        <Flex align="center" gap="sp24" maxW="420px" justify="space-between" width="100%">
          <Text fontFamily="heading" fontWeight="medium" fontSize="lg" color="text.title">
            {t("Sign-Ups Over Time")}
          </Text>

          <Flex sx={toggleContainerStyle} gap="8">
            <Button
              size="sm"
              borderRadius="16"
              py="8"
              px="16"
              sx={isWeeklySelected ? selectedButtonStyle : defaultButtonStyle}
              onClick={() => setAggregation("weekly")}
            >
              <Text
                fontWeight={isWeeklySelected ? "bold" : "regular"}
                fontSize="14px"
                color={isWeeklySelected ? colors.neutrals["0"] : "text.title"}
              >
                {t("Weekly")}
              </Text>
            </Button>
            <Button
              size="sm"
              borderRadius="16"
              py="8"
              px="16"
              sx={isMonthlySelected ? selectedButtonStyle : defaultButtonStyle}
              onClick={() => setAggregation("monthly")}
            >
              <Text
                fontWeight={isMonthlySelected ? "bold" : "regular"}
                fontSize="14px"
                color={isMonthlySelected ? colors.neutrals["0"] : "text.title"}
              >
                {t("Monthly")}
              </Text>
            </Button>
          </Flex>
        </Flex>
        {onDownload && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDownload(SIGN_UPS_CSV_COLUMNS, SIGN_UPS_CSV_FILENAME)}
            isDisabled={isLoading || isError || isDownloading || !data.data || data.data.length === 0}
          >
            <Icon icon={isDownloading ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-file-arrow-down"} mr="sp8" />
            <Text fontSize="12px" color="text.brand.400" fontWeight="medium">
              {isDownloading ? t("Downloading...") : t("Download")}
            </Text>
          </Button>
        )}
      </Flex>

      <Box height="280px" position="relative">
        {showLoading && (
          <Flex justify="center" align="center" height="100%">
            <ComponentLoading />
          </Flex>
        )}
        <Box id="signups-over-time-chart" width="100%" height="100%" visibility={showChart ? "visible" : "hidden"} />
      </Box>
    </Box>
  );
}

interface SignUpBucket {
  startDate: string;
  count: number;
}

export function aggregateSignUpData(dailyData: DailySignUpDataPoint[], aggregation: SignUpAggregation): AggregatedSignUpDataPoint[] {
  const filteredData: DailySignUpDataPoint[] = [];
  const cutoffDate = moment().subtract(11, "months");
  let accumulator = 0;

  for (const point of dailyData) {
    accumulator += point.newSignUps;
    if (moment(point.date).isAfter(cutoffDate)) {
      filteredData.push({
        date: point.date,
        newSignUps: accumulator,
        connectedStudents: point.connectedStudents,
      });
    }
  }

  if (filteredData.length === 0) {
    return getEmptyPlaceholderAggregatedData(aggregation);
  }

  const buckets = new Map<string, SignUpBucket>();
  for (const point of filteredData) {
    const pointDate = point.date;
    const pointNewSignUps = point.newSignUps;
    const date = moment(pointDate);
    let bucketKey: string;
    let startDate: string;

    if (aggregation === "monthly") {
      bucketKey = date.format("YYYY-MM");
      startDate = date.clone().startOf("month").format("YYYY-MM-DD");
    } else {
      // Weekly - use start of week (Monday)
      const weekStart = date.clone().startOf("isoWeek");
      bucketKey = weekStart.format("YYYY-[W]WW");
      startDate = weekStart.format("YYYY-MM-DD");
    }

    // Since we turned the daily data into cumulative data, we just need to
    // set the latest value to the given bucket key.
    buckets.set(bucketKey, { startDate, count: pointNewSignUps });
  }

  const data = Array.from(buckets.values());

  // For weekly, only show last 8 weeks
  const limitedData: SignUpBucket[] = aggregation === "weekly" ? data.slice(-8) : data;

  return limitedData.map(({ startDate, count }: SignUpBucket): AggregatedSignUpDataPoint => {
    const date = moment(startDate);
    let label: string;

    if (aggregation === "monthly") {
      label = date.format("MMM YYYY");
    } else {
      label = date.format("DD MMM YY");
    }

    return {
      startDate,
      count,
      label,
    };
  });
}

// This is to create a placeholder data when there is no data so that
// we can see the right labels.
function getEmptyPlaceholderAggregatedData(aggregation: SignUpAggregation): AggregatedSignUpDataPoint[] {
  const points: AggregatedSignUpDataPoint[] = [];
  const now = moment();

  if (aggregation === "monthly") {
    for (let i = 11; i >= 0; i--) {
      const date = now.clone().subtract(i, "months").startOf("month");
      points.push({
        startDate: date.format("YYYY-MM-DD"),
        count: 0,
        label: date.format("MMM YYYY"),
      });
    }
  } else {
    for (let i = 7; i >= 0; i--) {
      const weekStart = now.clone().subtract(i, "weeks").startOf("isoWeek");
      points.push({
        startDate: weekStart.format("YYYY-MM-DD"),
        count: 0,
        label: weekStart.format("DD MMM YY"),
      });
    }
  }
  return points;
}
